<script setup>
import { computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import WootReports from '../reports/components/WootReports.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();
const { checkPermissions, shouldShow } = usePolicy();

const currentUser = useMapGetter('getCurrentUser');
const getAgentById = useMapGetter('agents/getAgentById');

const isMemberHub = computed(() => Boolean(route.params.userId));

const targetUserId = computed(() =>
  isMemberHub.value
    ? Number(route.params.userId)
    : Number(currentUser.value?.id)
);

const canViewAllAgents = computed(() =>
  checkPermissions(['administrator', 'report_manage', 'report_agents'])
);

const canViewOwn = computed(() =>
  checkPermissions(['administrator', 'report_manage', 'report_own'])
);

const reportsEnabled = computed(() =>
  shouldShow(FEATURE_FLAGS.REPORTS, [
    'administrator',
    'report_manage',
    'report_own',
    'report_agents',
  ])
);

const isOwnProfile = computed(
  () =>
    !isMemberHub.value || targetUserId.value === Number(currentUser.value?.id)
);

const canAccess = computed(() => {
  if (!reportsEnabled.value) return false;
  if (isOwnProfile.value) return canViewOwn.value;
  return canViewAllAgents.value;
});

const redirectIfForbidden = () => {
  if (!currentUser.value?.id || canAccess.value) return;

  if (isMemberHub.value) {
    router.replace({
      name: 'profile_member_overview',
      params: {
        accountId: route.params.accountId,
        userId: route.params.userId,
      },
    });
    return;
  }

  router.replace({
    name: 'profile_settings_index',
    params: { accountId: route.params.accountId },
  });
};

watch([canAccess, () => route.fullPath], redirectIfForbidden, {
  immediate: true,
});

onMounted(() => {
  store.dispatch('agents/get').catch(() => {});
});

const agent = computed(() => {
  const id = targetUserId.value;
  if (!id) return null;

  const fromStore = getAgentById.value(id);
  if (fromStore?.id) return fromStore;

  if (Number(currentUser.value?.id) === id) {
    return currentUser.value;
  }

  return fromStore?.id ? fromStore : null;
});

const showDownload = computed(
  () => isMemberHub.value && !isOwnProfile.value && canViewAllAgents.value
);

const sectionTitle = computed(() => {
  if (isMemberHub.value && !isOwnProfile.value) {
    return t('AGENT_HUB.REPORTS_MEMBER_TITLE', {
      name: agent.value?.name || t('AGENT_HUB.UNKNOWN_NAME'),
    });
  }
  return t('SIDEBAR.REPORTS_OWN');
});

const sectionDescription = computed(() => {
  if (isMemberHub.value && !isOwnProfile.value) {
    return t('AGENT_HUB.REPORTS_MEMBER_DESCRIPTION');
  }
  return t('AGENT_HUB.REPORTS_INBOX_DESCRIPTION');
});
</script>

<template>
  <div v-if="!canAccess" />
  <div v-else-if="!agent?.id" class="py-16">
    <Spinner class="mx-auto" />
  </div>
  <div v-else class="flex flex-col gap-4">
    <div>
      <h2 class="text-heading-2 text-n-slate-12">
        {{ sectionTitle }}
      </h2>
      <p class="mt-1 text-sm text-n-slate-11">
        {{ sectionDescription }}
      </p>
    </div>
    <WootReports
      :key="agent.id"
      embedded
      type="agent"
      getter-key="agents/getAgents"
      action-key="agents/get"
      :selected-item="agent"
      :show-entity-filter="false"
      :show-download="showDownload"
      :download-button-label="t('AGENT_REPORTS.DOWNLOAD_AGENT_REPORTS')"
      :report-title="sectionTitle"
    />
  </div>
</template>
